import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Users, AlertCircle, Check, Info } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import InternationalPhoneInput from '../common/InternationalPhoneInput';
import AddressInput from '../common/AddressInput';
import { generateQRCodeWithLogo } from '@/utils/qrCodeWithLogo';
import { useEmailValidation } from '@/hooks/useEmailValidation';

interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  orderCount?: number;
  lastOrderDate?: string | null;
  isActive?: boolean;
}

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerCreated?: (customer: Customer) => void;
  mode?: 'standalone' | 'order'; // 'standalone' for customer tab, 'order' for order form
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({ 
  isOpen, 
  onClose, 
  onCustomerCreated,
  mode = 'standalone' 
}) => {
  const { user, token } = useAuth();
  const [customerModalMode, setCustomerModalMode] = useState<'select' | 'new' | 'link' | 'code'>('select');
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      coordinates: undefined
    }
  });
  const [linkingData, setLinkingData] = useState({
    linkUrl: '',
    qrCodeData: '',
    qrCodeImage: '',
    expiresAt: '',
    isGenerating: false
  });
  const [linkingCodeData, setLinkingCodeData] = useState({
    code: '',
    qrCodeImage: '',
    expiresAt: '',
    isGenerating: false
  });
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [customerSuccessMessage, setCustomerSuccessMessage] = useState('');
  
  // Real-time email validation
  const emailValidation = useEmailValidation(customerFormData.email.trim(), 500);

  const buildAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const handleCreateCustomer = async () => {
    if (!token || !customerFormData.name.trim()) {
      return;
    }

    // Check email validation before proceeding (only if email is provided)
    if (customerFormData.email.trim() && emailValidation.isAvailable === false) {
      setCustomerError(emailValidation.error || 'This email is already registered');
      return;
    }

    // Require either email or phone
    if (!customerFormData.email.trim() && !customerFormData.phone.trim()) {
      setCustomerError('Please provide either an email address or phone number');
      return;
    }

    setIsCreatingCustomer(true);
    setCustomerError('');
    try {
      // Create invitation (not a full user account)
      const requestData = {
        name: customerFormData.name.trim(),
        email: customerFormData.email.trim() || null,
        phone: customerFormData.phone.trim() || null
      };
      
      const response = await fetch(`${API_BASE}/api/tailors/customers/invite`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const invitationData = await response.json();
        
        // Create a temporary customer object for the callback
        const normalizedCustomer: Customer = {
          id: `invitation-${invitationData.invitation_id}`,
          name: customerFormData.name.trim(),
          email: invitationData.customer_email || undefined,
          phone: invitationData.customer_phone || undefined,
          orderCount: 0,
          lastOrderDate: null,
          isActive: false, // Not active until they register
        };

        // Show success message with invitation details
        const contactMethod = invitationData.customer_email ? 'email' : 'SMS';
        setCustomerSuccessMessage(
          `Invitation sent successfully via ${contactMethod}! The customer will receive a registration link to complete their account setup.`
        );
        
        // Reset form
        setCustomerFormData({ 
          name: '', 
          email: '', 
          phone: '', 
          address: {
            address: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            coordinates: undefined
          }
        });
        
        // Call the callback with the invitation customer
        if (onCustomerCreated) {
          onCustomerCreated(normalizedCustomer);
        }
        
        // Close modal after short delay to show success message
        setTimeout(() => {
          handleClose();
          setCustomerSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to create invitation. Please try again.';
        
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        }
        
        setCustomerError(errorMessage);
      }
    } catch (_error) {
      console.error('Error creating customer invitation:', error);
      setCustomerError('Network error occurred. Please check your connection and try again.');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handleGenerateLinkingCode = async () => {
    setLinkingCodeData(prev => ({ ...prev, isGenerating: true }));
    setCustomerError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/tailors/customers/generate-code`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        
        // Generate QR code image with MasterJi logo
        const qrCodeImage = await generateQRCodeWithLogo(data.qr_data, {
          width: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          logoSizeRatio: 0.2
        });
        
        setLinkingCodeData({
          code: data.code,
          qrCodeImage: qrCodeImage,
          expiresAt: data.expires_at,
          isGenerating: false
        });
        setCustomerModalMode('code');
      } else {
        const errorData = await response.json();
        setCustomerError(`Failed to generate code: ${errorData.detail || 'Unknown error'}`);
        setLinkingCodeData(prev => ({ ...prev, isGenerating: false }));
      }
    } catch (_error) {
      console.error('Error generating linking code:', error);
      setCustomerError('Network error occurred while generating code');
      setLinkingCodeData(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleGenerateLinkingLink = async () => {
    setLinkingData(prev => ({ ...prev, isGenerating: true }));
    setCustomerError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/tailors/customers/generate-link`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({
          tailor_id: user?.id,
          tailor_name: user?.name || user?.email,
          expires_in_hours: 24
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Generate QR code image with MasterJi logo
        const qrCodeImage = await generateQRCodeWithLogo(data.link_url, {
          width: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          logoSizeRatio: 0.2
        });
        
        setLinkingData({
          linkUrl: data.link_url,
          qrCodeData: data.qr_code_data,
          qrCodeImage: qrCodeImage,
          expiresAt: data.expires_at,
          isGenerating: false
        });
        setCustomerModalMode('link');
      } else {
        const errorData = await response.json();
        setCustomerError(`Failed to generate link: ${errorData.detail || 'Unknown error'}`);
        setLinkingData(prev => ({ ...prev, isGenerating: false }));
      }
    } catch (_error) {
      console.error('Error generating linking link:', error);
      setCustomerError('Network error occurred while generating link');
      setLinkingData(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleClose = () => {
    setCustomerModalMode('select');
    setCustomerFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      address: {
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        coordinates: undefined
      }
    });
    setLinkingData({ linkUrl: '', qrCodeData: '', qrCodeImage: '', expiresAt: '', isGenerating: false });
    setLinkingCodeData({ code: '', qrCodeImage: '', expiresAt: '', isGenerating: false });
    setCustomerError('');
    setCustomerSuccessMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {customerModalMode === 'select' ? 'Add Customer' : 
               customerModalMode === 'new' ? 'Add New Customer' : 
               customerModalMode === 'link' ? 'Link Existing Customer' :
               'Linking Code'}
            </h2>
            <p className="text-sm text-gray-600">
              {customerModalMode === 'select' ? 'Choose how to add this customer' :
               customerModalMode === 'new' ? 'Enter customer details to get started' :
               customerModalMode === 'link' ? 'Share this link with your customer to connect' :
               'Share this code or QR with your customer'}
            </p>
          </div>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
          {customerModalMode === 'select' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <p className="text-gray-600">How would you like to add this customer?</p>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <button
                  onClick={() => setCustomerModalMode('new')}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">New Customer</h3>
                      <p className="text-sm text-gray-600">Create a new customer account</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={handleGenerateLinkingLink}
                  disabled={linkingData.isGenerating}
                  className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors text-left disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      {linkingData.isGenerating ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" />
                      ) : (
                        <Users className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Existing Customer (Link)</h3>
                      <p className="text-sm text-gray-600">Generate a link for existing Masterji user</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={handleGenerateLinkingCode}
                  disabled={linkingCodeData.isGenerating}
                  className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      {linkingCodeData.isGenerating ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
                      ) : (
                        <Users className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Existing Customer (Code)</h3>
                      <p className="text-sm text-gray-600">Generate a code or QR for quick linking</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {customerModalMode === 'new' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={customerFormData.name}
                  onChange={(e) => setCustomerFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder="Enter customer name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={customerFormData.email}
                  onChange={(e) => {
                    setCustomerFormData(prev => ({ ...prev, email: e.target.value }));
                    // Clear error when user starts typing a new email
                    if (customerError.includes('email')) {
                      setCustomerError('');
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur if email is entered
                    if (customerFormData.email.trim() && emailValidation.error) {
                      setCustomerError(emailValidation.error);
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 ${
                    customerFormData.email.trim() && emailValidation.error
                      ? 'border-red-300 bg-red-50'
                      : customerFormData.email.trim() && emailValidation.isAvailable === true
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="customer@example.com"
                />
                <div className="mt-1">
                  {customerFormData.email.trim() && emailValidation.isLoading && (
                    <p className="text-xs text-gray-500">Checking email availability...</p>
                  )}
                  {customerFormData.email.trim() && !emailValidation.isLoading && emailValidation.error && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {emailValidation.error}
                    </p>
                  )}
                  {customerFormData.email.trim() && !emailValidation.isLoading && emailValidation.isAvailable === true && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Email is available
                    </p>
                  )}
                  {!customerFormData.email.trim() && (
                    <p className="text-xs text-gray-500">
                      Email is optional. You can provide either email or phone number. The customer will receive an invitation to register.
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone {customerFormData.email.trim() ? '' : '*'}
                </label>
                <InternationalPhoneInput
                  value={customerFormData.phone}
                  onChange={(phone, isValid) => {
                    setCustomerFormData(prev => ({ ...prev, phone }));
                  }}
                  placeholder="Enter phone number"
                  defaultCountry="in"
                />
                {!customerFormData.email.trim() && (
                  <p className="mt-1 text-xs text-gray-500">
                    Phone number is required if email is not provided.
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <AddressInput
                  value={customerFormData.address}
                  onChange={(addressData) => {
                    setCustomerFormData(prev => ({
                      ...prev,
                      address: addressData
                    }));
                  }}
                  placeholder="Enter customer address"
                  countryRestrictions={['in']}
                  apiKey={(import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY || ''}
                />
              </div>
            </>
          )}

          {customerModalMode === 'code' && linkingCodeData.code && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  {linkingCodeData.qrCodeImage ? (
                    <img 
                      src={linkingCodeData.qrCodeImage} 
                      alt="QR Code" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">QR Code</p>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Share this QR code or code with your customer
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Linking Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={linkingCodeData.code}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-center text-2xl font-bold text-gray-900"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(linkingCodeData.code)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-purple-600 mt-0.5" />
                  <div className="text-sm text-purple-800">
                    <p className="font-medium">What happens next?</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• Customer scans QR code or enters the code manually</li>
                      <li>• They'll be instantly linked to your account</li>
                      <li>• Code expires in 15 minutes</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="text-center text-xs text-gray-500">
                Code expires: {new Date(linkingCodeData.expiresAt).toLocaleString()}
              </div>
            </div>
          )}

          {customerModalMode === 'link' && linkingData.linkUrl && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  {linkingData.qrCodeImage ? (
                    <img 
                      src={linkingData.qrCodeImage} 
                      alt="QR Code" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">QR Code</p>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Share this QR code or link with your customer
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Share Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={linkingData.linkUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-900"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(linkingData.linkUrl)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">What happens next?</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• Customer scans QR code or clicks link</li>
                      <li>• They can share basic info (name, email, phone)</li>
                      <li>• They can optionally share measurements</li>
                      <li>• You'll be notified when they complete linking</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="text-center text-xs text-gray-500">
                Link expires: {new Date(linkingData.expiresAt).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Error and Success Messages */}
        {customerError && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{customerError}</span>
            </div>
          </div>
        )}
        
        {customerSuccessMessage && (
          <div className="px-6 py-3 bg-green-50 border-t border-green-200">
            <div className="flex items-center gap-2 text-green-800">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">{customerSuccessMessage}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          {customerModalMode === 'select' && (
            <button 
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          )}

          {customerModalMode === 'new' && (
            <>
              <button 
                type="button"
                onClick={() => setCustomerModalMode('select')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isCreatingCustomer}
              >
                Back
              </button>
              <button
                onClick={handleCreateCustomer}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                disabled={
                  isCreatingCustomer || 
                  !customerFormData.name.trim() ||
                  (!customerFormData.email.trim() && !customerFormData.phone.trim()) ||
                  (customerFormData.email.trim() && (emailValidation.isLoading || emailValidation.isAvailable === false))
                }
              >
                {isCreatingCustomer ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Customer
                  </>
                )}
              </button>
            </>
          )}

          {customerModalMode === 'code' && (
            <>
              <button 
                type="button"
                onClick={() => setCustomerModalMode('select')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                Done
              </button>
            </>
          )}

          {customerModalMode === 'link' && (
            <>
              <button 
                type="button"
                onClick={() => setCustomerModalMode('select')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Done
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AddCustomerModal;

